import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useI18n } from '../i18n/i18nContext';
import LocalizedLink from '../components/LocalizedLink';
import { axiosClient, getImageUrl } from '../services/api';
import { Service } from '@shared/types';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { pushToDataLayer, formatServiceItem } from '../utils/dataLayer';
import SEO from '../components/SEO';

interface ExtendedService extends Service {
    slug: string;
    whatIs?: Record<string, string>;
    videoHeading?: Record<string, string>;
    videoUrl?: string;
    videoThumbnailUrl?: string;
    benefitsHeading?: Record<string, string>;
    benefits?: Record<string, string>;
    howItWorksHeading?: Record<string, string>;
    howItWorksBody?: Record<string, string>;
    experienceHeading?: Record<string, string>;
    experienceBody?: Record<string, string>;
    images?: string[];
}

const ServicePage: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const { language, t } = useI18n();
    
    const [service, setService] = useState<ExtendedService | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const serviceName = service?.[`name_${language}` as keyof Service] as string || service?.name || '';
    // Append localized "in Tallinn" to service name for SEO
    const pageTitle = serviceName ? `${serviceName} ${t.common.inTallinn}` : '';
    useDocumentTitle(pageTitle);

    useEffect(() => {
        if (!slug) return;
        const fetchService = async () => {
            setLoading(true);
            setError(false);
            try {
                const response = await axiosClient.get<ExtendedService>(`/api/services/by-slug/${slug}`);
                setService(response.data);
            } catch (err) {
                console.error('Failed to fetch service:', err);
                setError(true);
            }
            setLoading(false);
        };
        fetchService();
    }, [slug]);

    useEffect(() => {
        if (service) {
            pushToDataLayer('view_service', {
                content: { category: 'booking_service' },
                ecommerce: {
                    items: [formatServiceItem(service)]
                }
            });
        }
    }, [service]);

    const getText = (field: Record<string, string> | undefined) => {
        if (!field) return '';
        return field[language] || field['en'] || '';
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-16">
                <div className="text-center text-gray-500">{t.common.loading}</div>
            </div>
        );
    }

    if (error || !service) {
        return (
            <div className="container mx-auto px-4 py-16">
                <div className="text-center">
                    <h1 className="text-2xl font-bold mb-4">Service not found</h1>
                    <LocalizedLink to="/pricing" className="text-blue-600 hover:text-blue-700">
                        {t.common.goHome}
                    </LocalizedLink>
                </div>
            </div>
        );
    }

    // Get description safely based on slug
    const seoKey = slug as keyof typeof t.seo.services;
    const metaDescription = t.seo.services?.[seoKey] || t.seo.defaultDescription;

    return (
        <div>
            <SEO description={metaDescription} />
            <section
                className="relative text-white py-28 md:py-56 overflow-hidden"
                style={{
                    backgroundImage: service.videoUrl
                        ? 'linear-gradient(to bottom, rgb(17, 24, 39), rgb(31, 41, 55))'
                        : service.videoThumbnailUrl
                            ? `url(${getImageUrl(service.videoThumbnailUrl)})`
                            : 'linear-gradient(to bottom, rgb(17, 24, 39), rgb(31, 41, 55))',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                {service.videoUrl && (
                    <>
                        <video
                            autoPlay
                            muted
                            loop
                            playsInline
                            poster={service.videoThumbnailUrl ? getImageUrl(service.videoThumbnailUrl) : undefined}
                            className="absolute inset-0 w-full h-full object-cover z-0"
                        >
                            <source src={service.videoUrl} type="video/mp4" />
                        </video>
                        <div className="absolute inset-0 bg-black/50 z-10"></div>
                    </>
                )}
                {!service.videoUrl && service.videoThumbnailUrl && (
                    <div className="absolute inset-0 bg-black/50 z-10"></div>
                )}
                <div className="container mx-auto px-4 relative z-20">
                    <div className="max-w-4xl mx-auto text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">{serviceName}</h1>
                        <LocalizedLink
                            to={`/book/${service.id}`}
                            className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-10 py-4 rounded-lg text-lg font-semibold transition-colors shadow-lg"
                        >
                            {t.servicePage?.bookNow || 'Book Now'}
                        </LocalizedLink>
                    </div>
                </div>
            </section>


            <div className="container mx-auto px-4 py-12 max-w-6xl">
                {getText(service.whatIs) && (
                    <section className="mb-16 text-center">
                        {getText(service.videoHeading) && (
                            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                                {getText(service.videoHeading)}
                            </h2>
                        )}
                        <div className="max-w-3xl mx-auto">
                            <p className="text-lg text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: getText(service.whatIs) }} />
                        </div>
                    </section>
                )}

            </div>

            {getText(service.benefits) && (
                <section className="mb-16">
                    {service.images && service.images.length > 0 && (
                        <div
                            className="relative w-full h-96 md:h-[500px]"
                            style={{
                                backgroundImage: `url(${getImageUrl(service.images[2])})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                            }}
                        >
                            <div className="absolute inset-0 bg-black/50"></div>
                            <div className="relative z-10 h-full flex flex-col justify-center">
                                <div className="container mx-auto px-4 md:px-4 max-w-6xl">
                                    <div className="max-w-2xl pl-4 md:pl-0">
                                {getText(service.benefitsHeading) && (
                                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                                        {getText(service.benefitsHeading)}
                                    </h2>
                                )}
                                <ul
                                    className="space-y-3 text-white [&>li]:flex [&>li]:items-start [&>li]:space-x-3 [&>li>p]:mb-0 [&>li]:leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: getText(service.benefits)
                                            .replace(/<li/g, '<li class="flex items-start space-x-3"')
                                            .replace(/<li class="flex items-start space-x-3"/g, '<li class="flex items-start space-x-3"><span class="flex-shrink-0 w-2 h-2 bg-white rounded-full mt-2"></span><div')
                                            .replace(/<\/li>/g, '</div></li>')
                                    }}
                                />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {(!service.images || service.images.length === 0) && (
                        <div className="container mx-auto px-4 max-w-6xl">
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 md:p-12 rounded-2xl shadow-lg">
                                {getText(service.benefitsHeading) && (
                                    <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
                                        {getText(service.benefitsHeading)}
                                    </h2>
                                )}
                                <ul
                                    className="space-y-3 text-gray-700 [&>li]:flex [&>li]:items-start [&>li]:space-x-3 [&>li>p]:mb-0"
                                    dangerouslySetInnerHTML={{
                                        __html: getText(service.benefits)
                                            .replace(/<li/g, '<li class="flex items-start space-x-3"')
                                            .replace(/<li class="flex items-start space-x-3"/g, '<li class="flex items-start space-x-3"><span class="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></span><div')
                                            .replace(/<\/li>/g, '</div></li>')
                                    }}
                                />
                            </div>
                        </div>
                    )}
                </section>
            )}

            <div className="container mx-auto px-4 py-12 max-w-6xl">

                {getText(service.howItWorksBody) && (
                    <section className="mb-16">
                        <div className="flex items-start gap-8">
                            {/* How it Works Icon/Image */}
                            <div className="hidden md:block flex-shrink-0">
                                {service.images && service.images.length > 0 ? (
                                    <img
                                        src={getImageUrl(service.images[0])}
                                        alt={serviceName}
                                        className="w-[80px] h-[120px] object-contain"
                                    />
                                ) : (
                                    <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 text-sm font-bold">Icon</span>
                                    </div>
                                )}
                            </div>
                            {/* How it Works Text */}
                            <div className="flex-1 space-y-6">
                                {getText(service.howItWorksHeading) && (
                                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                                        {getText(service.howItWorksHeading)}
                                    </h2>
                                )}
                                <div
                                    className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: getText(service.howItWorksBody) }}
                                />
                                {/* Colored line separator - blue, wider, and centered */}
                                <div className="w-3/5 h-0.5 bg-blue-800 mt-12 mx-auto"></div>
                            </div>
                        </div>
                    </section>
                )}
                
                {getText(service.experienceBody) && (
                    <section className="mb-16">
                        {/* Experience Image in its own row */}
                        {service.images && service.images.length > 1 && (
                            <div className="mb-8 flex justify-center">
                                <img
                                    src={getImageUrl(service.images[1])}
                                    alt={serviceName}
                                    className="w-full md:w-5/6 h-auto md:rounded-2xl object-cover"
                                />
                            </div>
                        )}

                        {/* Content Block with light background color */}
                        <div
                            className="p-8 md:p-12 rounded-2xl"
                            style={{ backgroundColor: 'rgba(229, 240, 254, 0.8)' }}
                        >
                            {getText(service.experienceHeading) && (
                                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
                                    {getText(service.experienceHeading)}
                                </h2>
                            )}
                            <ul className="space-y-4 text-gray-700">
                                {getText(service.experienceBody)
                                    .split('<li>')
                                    .filter(item => item.trim())
                                    .map((item, index) => {
                                        const cleanItem = item.replace(/<\/li>.*/, '').replace(/<[^>]*>/g, '').trim();
                                        if (!cleanItem) return null;
                                        return (
                                            <li key={index} className="flex items-start space-x-3">
                                                <span className="flex-shrink-0 w-2 h-2 bg-blue-600 rounded-full mt-2"></span>
                                                <span className="leading-relaxed">{cleanItem}</span>
                                            </li>
                                        );
                                    })
                                    .filter(Boolean)
                                }
                            </ul>
                        </div>
                    </section>
                )}

                <section className="text-center py-12">
                    <LocalizedLink
                        to={`/book/${service.id}`}
                        className="inline-block bg-blue-600 hover:bg-blue-700 text-white px-12 py-5 rounded-lg text-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                    >
                        {t.servicePage?.bookNow || 'Book Now'}
                    </LocalizedLink>
                </section>
            </div>
        </div>
    );
};

export default ServicePage;
